import { Box, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fromLonLat } from 'ol/proj';
import Overlay from 'ol/Overlay';
import Map from 'ol/Map';
import View from 'ol/View';
import { Zoom } from 'ol/control';
import WebGLTile from 'ol/layer/WebGLTile';
import 'ol/ol.css';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { MAPBOX_STYLE_TILE } from '../constants/mapbox';
import SliderBox from './SliderBox';
import { latlngType } from '../types';
import VectorSource from 'ol/source/Vector';
import tBlue from '../../../assets/t_blue.png';
import tOrange from '../../../assets/t_orange.png';
import pin from '../../../assets/pin.png';
import current from '../../../assets/current.png';

import VectorLayer from 'ol/layer/Vector';
import {
  drawAreaFromAlert,
  drawPinFromLonlat,
  drawPointFromAlert,
} from '../functions/drawAreasFromAlerts';
import { alertListType } from 'features/alerts/types';
import { colorStyle } from '../constants/colorStyle';
import { Station } from '../api/getStations';
import { Vector } from 'ol/source';
import { Feature, MapBrowserEvent } from 'ol';
import Cluster from 'ol/source/Cluster';
import { Point } from 'ol/geom';
import { Icon, Style, Text, Fill, Stroke } from 'ol/style';
import type { FeatureLike } from 'ol/Feature';
import { useIsMobile } from '../../../hooks/useIsMobile';
import FloodDepthMeter from './FloodDepthMeter';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import TimeSelectSlider from './TimeSelectSlider';
import { sendAnalyticsEvent } from '../../../utils/googleAnalytics';


const MAPBOX_LAYER_TYPE = {
  default: 'default',
  dark: 'dark',
  satellite: 'satellite',
};

const MAPBOX_LAYER_TYPE_SELECT_MENUITEMS = [
  { label: 'common.label.default', value: MAPBOX_LAYER_TYPE.default },
  { label: 'common.label.dark', value: MAPBOX_LAYER_TYPE.dark },
  { label: 'common.label.satellite', value: MAPBOX_LAYER_TYPE.satellite },
];

const LOCALE_TYPE = {
  ja: 'ja',
  en: 'en',
};

const MAPBOX_LAYERS = [
  {
    lang: LOCALE_TYPE.ja,
    type: MAPBOX_LAYER_TYPE.default,
    url: MAPBOX_STYLE_TILE.ja.default,
  },
  {
    lang: LOCALE_TYPE.ja,
    type: MAPBOX_LAYER_TYPE.dark,
    url: MAPBOX_STYLE_TILE.ja.dark,
  },
  {
    lang: LOCALE_TYPE.ja,
    type: MAPBOX_LAYER_TYPE.satellite,
    url: MAPBOX_STYLE_TILE.ja.satellite,
  },
  {
    lang: LOCALE_TYPE.en,
    type: MAPBOX_LAYER_TYPE.default,
    url: MAPBOX_STYLE_TILE.en.default,
  },
  {
    lang: LOCALE_TYPE.en,
    type: MAPBOX_LAYER_TYPE.dark,
    url: MAPBOX_STYLE_TILE.en.dark,
  },
  {
    lang: LOCALE_TYPE.en,
    type: MAPBOX_LAYER_TYPE.satellite,
    url: MAPBOX_STYLE_TILE.en.satellite,
  },
];

type PredictionMapProps = {
  latlng: latlngType;
  style?: any;
  alerts: alertListType;
  goToCurrentLocation: () => void;
  pins?: Station[];
  onClick?: (index: number) => void;
  increment: number;
  setIncrement: React.Dispatch<React.SetStateAction<number>>;
  isUrlLoading: boolean;
  isImageIdChecking: boolean;
  showFloodDepthControls?: boolean;
};

export type PredictionMapHandle = {
  updateGeeUrl: (nextGeeUrl: string) => void;
};

const blueIconStyle = new Style({
  image: new Icon({
    src: tBlue,
    scale: 1,
  }),
});

const orangeIconStyle = new Style({
  image: new Icon({
    src: tOrange,
    scale: 1,
  }),
});

const pinIcon = new Style({
  image: new Icon({
    src: pin,
    scale: 1,
  }),
});

const currentIcon = new Style({
  image: new Icon({
    src: current,
    scale: 1,
  }),
});

const PredictionMap = React.forwardRef<PredictionMapHandle, PredictionMapProps>(({ 
  latlng,
  style = {},
  alerts,
  goToCurrentLocation,
  pins = [],
  onClick,
  increment,
  setIncrement,
  isUrlLoading,
  isImageIdChecking,
  showFloodDepthControls = true,
}, ref) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const isMobile = useIsMobile();

  const mapRef = useRef<never>(null);
  const overlayRef = useRef<Overlay | null>(null);
  const popupRef = useRef<any | null>(null);

  const mapObjectRef = useRef<Map | null>(null);
  const layerRef = useRef<WebGLTile | null>(null);
  const currentGeeUrlRef = useRef('');
  const [hasFloodLayer, setHasFloodLayer] = useState(false);
  const [popupContent, setPopupContent] = useState<ReactNode>(null);
  const [hoveredStation, setHoveredStation] = useState<Station | null>(null);
  const [isSettingMenu, setIsSettingMenu] = useState(false);
  // 浸水深レイヤーの透過率
  const [floodOpacity, setFloodOpacity] = useState(() => {
    const saved = localStorage.getItem('predictionMap.floodOpacity');
    return saved ? parseFloat(saved) : 0.7;
  });

  // マップの種類（ダーク、航空、通常）の切り替えフォーム
  const [layerType, setLayerType] = useState(() => {
    const saved = localStorage.getItem('predictionMap.layerType');
    return saved || MAPBOX_LAYER_TYPE.default;
  });

  const cache = useRef<{ map: Map | null, currentMarker: VectorLayer<VectorSource<Feature<any>>> | null, layers: TileLayer<XYZ>[] }>({ map: null, currentMarker: null, layers: [] });

  const mapStyleTile = React.useMemo(() => {
    const lang = language as 'ja' | 'en';
    return MAPBOX_STYLE_TILE[lang].default;
  }, [language]);

  const updateGeeUrl = React.useCallback((nextGeeUrl: string) => {
    currentGeeUrlRef.current = nextGeeUrl;

    const mapObject = mapObjectRef.current;
    if (!mapObject) {
      return;
    }

    const currentLayer = layerRef.current;
    if (!nextGeeUrl) {
      if (currentLayer) {
        mapObject.removeLayer(currentLayer);
        layerRef.current = null;
      }
      setHasFloodLayer(false);
      return;
    }

    const source = new XYZ({
      url: nextGeeUrl,
    });

    const geelayer = new WebGLTile({
      style: colorStyle,
      source,
      maxZoom: undefined,
      zIndex: 2,
    });

    geelayer.setOpacity(floodOpacity);
    mapObject.addLayer(geelayer);

    if (currentLayer) {
      currentLayer.setOpacity(floodOpacity);
      source.once('tileloadend', () => {
        mapObject.removeLayer(currentLayer);
      });
    }

    layerRef.current = geelayer;
    setHasFloodLayer(true);
  }, [floodOpacity]);

  React.useImperativeHandle(ref, () => ({
    updateGeeUrl,
  }), [updateGeeUrl]);

  // latlngが変わったときにベースの地図を変える
  useEffect(() => {
    if (!popupRef.current) {
      return;
    }
    overlayRef.current = new Overlay({
      element: popupRef.current,
      autoPan: {
        animation: {
          duration: 150,
        },
      },
    });

    const handleSingleclick = (e: MapBrowserEvent<any>) => {
      if (cache.current.map) {
        cache.current.map.forEachFeatureAtPixel(e.pixel, (feature, layer) => {
          // クラスタの場合はfeature.get('features')が配列
          const features = feature.get('features');
          if (features && features.length === 1) {
            if (onClick) {
              onClick(features[0].get('index'));
            }
            const marker = new VectorLayer({
              source: new Vector({
                features: [new Feature({
                  geometry: features[0].get('geometry'),
                })],
              }),
              style: orangeIconStyle,
              zIndex: 3,
            });
            if (cache.current.map) {
              if (cache.current.currentMarker) {
                cache.current.map.removeLayer(cache.current.currentMarker);
              }
              cache.current.map.addLayer(marker);
            }
            cache.current.currentMarker = marker;
          }
        });
      }
    };

    const centerStr = localStorage.getItem('predictionMap.center') || null;
    const zoomStr = localStorage.getItem('predictionMap.zoom') || null;
    const center = latlng
      ? fromLonLat([latlng[1], latlng[0]])
      : centerStr
      ? centerStr.split(',').map(d => Number(d))
      : fromLonLat([139.767221, 35.680885]);
    const zoom = zoomStr ? Number(zoomStr) : 10;

    // alertsに登録された地点の追加
    const rectangles = alerts
      .filter(alert => !!alert.is_area)
      .map(alert => drawAreaFromAlert(alert));
    const points = alerts.filter(alert => !alert.is_area).map(alert => drawPointFromAlert(alert));
    const currentLocationPin = drawPinFromLonlat(latlng);
    currentLocationPin.setStyle(currentIcon);

    const vectorSource = new VectorSource({
      features: [...points, ...rectangles, currentLocationPin],
    });

    const vectorLayer = new VectorLayer({
      source: vectorSource,
      zIndex: 3,
    });

    // pinsクラスタリング
    const pinFeatures = pins.map((p: Station, index) => {
      const title = `${p.basin_name || ''} ${p.river_name || ''} ${p.station_name || ''}`;
      const feature = new Feature({
        geometry: new Point(fromLonLat([Number(p.lon), Number(p.lat)])),
        index,
        title,
        station: p,
      });
      return feature;
    });
    const pinVectorSource = new VectorSource({ features: pinFeatures });
    const clusterSource = new Cluster({
      distance: 40, // ピクセル単位のクラスタ距離
      source: pinVectorSource,
    });

    // クラスタのスタイル
    const clusterStyle = (feature: FeatureLike) => {
      const features = feature.get('features') as Feature[];
      const size = features.length;
      if (size === 1) {
        return blueIconStyle;
      } else {
        // 複数個クラスタの場合は円＋数字
        return new Style({
          image: new Icon({
            src: tBlue,
            scale: 0.2,
          }),
          text: new Text({
            text: size.toString(),
            scale: 1.2,
            fill: new Fill({ color: '#fff' }),
            stroke: new Stroke({ color: '#1976d2', width: 3 }),
            offsetY: 0,
          }),
        });
      }
    };

    const clusterLayer = new VectorLayer({
      source: clusterSource,
      style: clusterStyle,
      zIndex: 3,
    });

    const layers = MAPBOX_LAYERS.map(
      (d) =>
        new TileLayer({
          source: new XYZ({
            attributions: '© <a href="https://www.mapbox.com/map-feedback/">Mapbox</a>',
            url: d.url,
            crossOrigin: 'anonymous',
          }),
          zIndex: 1,
          opacity: 0,
          visible: true,
        }),
    );

    const olMapObj = new Map({
      layers: [...layers, vectorLayer, clusterLayer],
      view: new View({
        center,
        zoom,
        maxZoom: undefined,
      }),
      controls: [new Zoom({ className: isMobile ? 'custom-map-zoom-mobile' : 'custom-map-zoom' })],
      overlays: overlayRef.current ? [overlayRef.current] : [],
    });
    let lastInteractionType = 'programmatic';
    let previousZoom = zoom;
    let isFirstMoveend = true;

    const viewport = olMapObj.getViewport();
    const handleWheel = () => {
      lastInteractionType = 'wheel_zoom';
    };
    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        lastInteractionType = 'pinch_zoom';
      }
    };
    viewport.addEventListener('wheel', handleWheel, { passive: true });
    viewport.addEventListener('touchstart', handleTouchStart, { passive: true });
    if (mapRef.current) {
      olMapObj.setTarget(mapRef.current);
      const index = MAPBOX_LAYERS.findIndex((d) => d.lang === language && d.type === layerType) ?? 0;
      layers[index].setOpacity(1);
    }
    olMapObj.on('pointerdrag', () => {
      lastInteractionType = 'drag';
    });

    olMapObj.on('moveend', e => {
      let zoom = olMapObj.getView().getZoom();
      let center = olMapObj.getView().getCenter();
      if (zoom !== undefined && center) {
        localStorage.setItem('predictionMap.zoom', String(zoom));
        localStorage.setItem('predictionMap.center', String(center));
        if (isFirstMoveend) {
          isFirstMoveend = false;
          previousZoom = zoom;
          return;
        }
        // GA4: 地図移動・ズーム操作の完了を計測
        sendAnalyticsEvent('map_interaction', {
          action: 'moveend',
          interaction_type: lastInteractionType,
          zoom_level: Math.round(zoom * 10) / 10,
          zoom_changed: previousZoom !== zoom,
        });
        previousZoom = zoom;
        lastInteractionType = 'programmatic';
      }
    });

    olMapObj.on('pointermove', e => {
      let isOnFeature = false;
      olMapObj.forEachFeatureAtPixel(e.pixel, (f): void => {
        // クラスタfeature
        const features = f.get('features');
        if (features && features.length === 1) {
          const hoveredFeature = features[0];
          if (hoveredFeature && hoveredFeature.get('title') && hoveredFeature.get('station')) {
            const coordinate = e.coordinate;
            isOnFeature = true;
            const station: Station = hoveredFeature.get('station');
            setHoveredStation(station);
            setPopupContent(
              <Stack>
                <Typography>水系名：{station.basin_name}</Typography>
                <Typography>河川名：{station.river_name}</Typography>
                <Typography>地点名：{station.station_name}</Typography>
              </Stack>
            );
            overlayRef.current?.setPosition(coordinate);
          }
        }
      });
      if (!isOnFeature) {
        overlayRef.current?.setPosition(undefined);
        setHoveredStation(null);
        setPopupContent(null);
      }
    });

    olMapObj.on('singleclick', handleSingleclick);

    cache.current.map = olMapObj;
    cache.current.layers = layers;

    mapObjectRef.current = olMapObj;

    if (currentGeeUrlRef.current) {
      updateGeeUrl(currentGeeUrlRef.current);
    }

    if (!!olMapObj) {
      olMapObj.getViewport().style.cursor = 'pointer';
    }

    return () => {
      // OpenLayersの地図インスタンスのターゲット解除
      if (olMapObj && olMapObj.getTargetElement()) {
        viewport.removeEventListener('wheel', handleWheel);
        viewport.removeEventListener('touchstart', handleTouchStart);
        olMapObj.setTarget(undefined);
      }
      mapObjectRef.current = null;
      layerRef.current = null;
      setHasFloodLayer(false);
    };
  }, [latlng, mapStyleTile, alerts, pins, updateGeeUrl]);

  // 浸水深レイヤーの透過率変更時にlocalStorageへ保存
  useEffect(() => {
    localStorage.setItem('predictionMap.floodOpacity', String(floodOpacity));
    if (layerRef.current) {
      layerRef.current.setOpacity(floodOpacity);
    }
  }, [floodOpacity]);

  // layerTypeが変わったらマップのレイヤーを変える
  useEffect(() => {
    const index = MAPBOX_LAYERS.findIndex((d) => d.lang === language && d.type === layerType) ?? 0;

    let i = 0;

    for (const layer of cache.current.layers) {
      // すでに表示済のlayerをvisible->falseにする
      if (layer.getVisible() && i !== index) {
        layer.setOpacity(0);
      }

      if (i === index) {
        layer.setOpacity(1);
      }

      i += 1;
    }
    // 選択状態を保存
    localStorage.setItem('predictionMap.layerType', layerType);
  }, [language, layerType]);

  return (
    <Box position="relative">
      <div ref={mapRef} style={{ width: '100%', height: 'calc(100vh - 60px)', ...{ ...style } }} />

      {showFloodDepthControls && (
        <Stack
          position="absolute"
          zIndex={3}
          sx={isMobile ? { bottom: 290, right: 16 } : { bottom: 138, left: 24 }}
          spacing={2}
          alignItems={isMobile ? 'flex-end' : 'flex-start'}
        >
          <FloodDepthMeter />
        </Stack>
      )}
      { 0 < increment && 
      <Box
        sx={{
          position: 'absolute',
          zIndex: 2,
          border: '1px solid #ccc',
          backgroundColor: '#fff',
          padding: '8px 16px',
          ...(isMobile
            ? { width: 'calc(100% - 8px)', maxWidth: '400px', height: '170px', margin: '0 auto', left: 0, right: 0, bottom: 4 }
            : { width: 320, top: 132, left: 24 }
          )
        }}
      >
        <TimeSelectSlider
          setIncrement={setIncrement}
          increment={increment}
          isUrlLoading={isUrlLoading}
          isImageIdChecking={isImageIdChecking}
        />
      </Box>
      }

      {showFloodDepthControls && (!isSettingMenu ? (
        <Stack sx={{
          position: 'absolute',
          zIndex: 3,
          width: 40,
          height: 40,
          backgroundColor: 'rgba(0, 0, 0, 0.54)',
          borderRadius: 2,
          ...(isMobile
            ? { bottom: 232, right: 24, }
            : { bottom: 76, left: 24 }
          )
        }}>
          <IconButton onClick={() => {
            // GA4: 地図設定メニューを開いた操作を計測
            sendAnalyticsEvent('map_settings', { action: 'open' });
            setIsSettingMenu(true);
          }}>
            <SettingsIcon sx={{ color: '#fff' }} />
          </IconButton>
        </Stack>
      ) : (
        <Stack
          sx={{
            position: 'absolute',
            overflow: 'visible',
            width: 300,
            zIndex: 3,
            backgroundColor: 'rgba(0, 0, 0, 0.54)',
            padding: '8px 24px 16px',
            borderRadius: 2,
            boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            ...(isMobile
              ? { bottom: 180, left: 0, right: 0, margin: '0 auto', }
              : { bottom: 24, left: 24 }
            )
          }}
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={2}
        >
          <SliderBox target={hasFloodLayer ? layerRef.current : null} initialValue={floodOpacity} onOpacityChange={setFloodOpacity} />

          <Stack>
            <Typography sx={{ color: '#fff', textShadow: '0 1px 2px #333', fontWeight: 'bold', fontSize: 12, mb: 1 }}>{t('', '地図の選択')}</Typography>
            <Select
              name="layerType"
              value={layerType}
              size="small"
              sx={{
                backgroundColor: '#fff',
              }}
              onChange={(e) => {
                // GA4: 地図種別の変更を計測
                sendAnalyticsEvent('map_settings', {
                  action: 'change_layer_type',
                  layer_type: e.target.value,
                });
                setLayerType(e.target.value);
              }}
                  // GA4: 地図設定メニューを閉じた操作を計測
            >
              {MAPBOX_LAYER_TYPE_SELECT_MENUITEMS.map((d) => (
                <MenuItem key={d.label} value={d.value}>
                  {t(d.label)}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          <IconButton
            onClick={() => {
              sendAnalyticsEvent('map_settings', { action: 'close' });
              setIsSettingMenu(false);
            }}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 22,
              height: 22,
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.72)',
              backgroundColor: 'rgba(255,255,255,0.08)',
              p: 0,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.16)',
              },
            }}
            aria-label="close-slider-menu"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      ))}
      
      <Box
        sx={{
          position: 'absolute',
          backgroundColor: '#fff',
          padding: '16px',
          border: '1px solid #ccc',
          left: '-50px',
          bottom: '12px',
          minWidth: '140px',
          '&:after': {
            top: '100%',
            border: 'solid transparent',
            content: '" "',
            height: 0,
            width: 0,
            position: 'absolute',
            pointerEvents: 'none',
            borderTopColor: 'white',
            borderWidth: '10px',
            left: '48px',
            marginLeft: '-10px',
          },
          '&:before': {
            top: '100%',
            border: 'solid transparent',
            content: '" "',
            height: 0,
            width: 0,
            position: 'absolute',
            pointerEvents: 'none',
            borderTopColor: '#cccccc',
            borderWidth: '11px',
            left: '48px',
            marginLeft: '-11px',
          },
        }}
        ref={popupRef}
      >
        {popupContent}
      </Box>
    </Box>
  );
});

const areStationsEqual = (prevPins: Station[] = [], nextPins: Station[] = []) => {
  if (prevPins === nextPins) {
    return true;
  }
  if (prevPins.length !== nextPins.length) {
    return false;
  }
  for (let i = 0; i < prevPins.length; i += 1) {
    if (prevPins[i]?.id !== nextPins[i]?.id) {
      return false;
    }
  }
  return true;
};

const MemoizedPredictionMap = React.memo(PredictionMap, (prev, next) => {
  return (
    prev.latlng === next.latlng
    && prev.style === next.style
    && prev.alerts === next.alerts
    && prev.goToCurrentLocation === next.goToCurrentLocation
    && prev.onClick === next.onClick
    && prev.increment === next.increment
    && prev.setIncrement === next.setIncrement
    && prev.isUrlLoading === next.isUrlLoading
    && prev.isImageIdChecking === next.isImageIdChecking
    && prev.showFloodDepthControls === next.showFloodDepthControls
    && areStationsEqual(prev.pins, next.pins) 
  );
});

export default MemoizedPredictionMap;
